import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import {
  FlooringCalculatorInputs,
  FlooringCalculatorResults,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface FlooringCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const FlooringCalculator: React.FC<FlooringCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');
  const [inputs, setInputs] = useState<FlooringCalculatorInputs>({
    roomLength: '',
    roomWidth: '',
    wastePercentage: 10,
    sqftPerBox: '',
    plankLength: '',
    plankWidth: '',
  });

  const [results, setResults] = useState<FlooringCalculatorResults | null>(
    null,
  );
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof FlooringCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputs((prev) => ({
        ...prev,
        [field]: field === 'wastePercentage' ? Number(value) : value,
      }));
    };

  const handleCalculate = () => {
    setResults(null);

    const parsedRoomLength = parseFloat(inputs.roomLength);
    const parsedRoomWidth = parseFloat(inputs.roomWidth);
    const parsedWastePercentage = parseFloat(inputs.wastePercentage.toString());
    const parsedSqftPerBox = parseFloat(inputs.sqftPerBox);
    const parsedPlankLength = parseFloat(inputs.plankLength);
    const parsedPlankWidth = parseFloat(inputs.plankWidth);

    if (isNaN(parsedRoomLength) || parsedRoomLength <= 0) {
      return;
    }
    if (isNaN(parsedRoomWidth) || parsedRoomWidth <= 0) {
      return;
    }
    if (isNaN(parsedWastePercentage) || parsedWastePercentage < 0) {
      return;
    }
    if (isNaN(parsedSqftPerBox) || parsedSqftPerBox <= 0) {
      return;
    }

    if (
      (inputs.plankLength || inputs.plankWidth) &&
      (isNaN(parsedPlankLength) ||
        parsedPlankLength <= 0 ||
        isNaN(parsedPlankWidth) ||
        parsedPlankWidth <= 0)
    ) {
      return;
    }

    try {
      const calculatedRoomArea = parsedRoomLength * parsedRoomWidth;

      const wasteFactor = 1 + parsedWastePercentage / 100;
      const calculatedFlooringNeeded = calculatedRoomArea * wasteFactor;

      const calculatedBoxesNeeded = Math.ceil(
        calculatedFlooringNeeded / parsedSqftPerBox,
      );

      let calculatedPlanksNeeded: number | null = null;
      if (parsedPlankLength && parsedPlankWidth) {
        const plankArea = (parsedPlankLength / 12) * (parsedPlankWidth / 12);
        calculatedPlanksNeeded = Math.ceil(calculatedFlooringNeeded / plankArea);
      }

      setResults({
        roomArea: calculatedRoomArea.toFixed(2),
        flooringNeeded: calculatedFlooringNeeded.toFixed(2),
        boxesNeeded: calculatedBoxesNeeded,
        planksNeeded: calculatedPlanksNeeded,
      });
    } catch (error) {
      console.error('Calculation failed:', error);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.flooring}
      </h3>

      {/* Room Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.dimensions.roomDimensionsFt}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.dimensions.length} htmlFor="roomLength" />
            <Input
              id="roomLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 15`}
              value={inputs.roomLength}
              onChange={handleInputChange('roomLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.width} htmlFor="roomWidth" />
            <Input
              id="roomWidth"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 12`}
              value={inputs.roomWidth}
              onChange={handleInputChange('roomWidth')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Waste & Box Information */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.flooring.wasteAndBoxInformation}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.dimensions.wasteAllowancePct} htmlFor="wastePercentage" />
            <Input
              id="wastePercentage"
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              placeholder={`${tCalc.actions.examplePrefix}, 10`}
              value={inputs.wastePercentage}
              onChange={handleInputChange('wastePercentage')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.flooring.sqftPerBox} htmlFor="sqftPerBox" />
            <Input
              id="sqftPerBox"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 20`}
              value={inputs.sqftPerBox}
              onChange={handleInputChange('sqftPerBox')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Wood Plank Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.flooring.woodPlankDimensions}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.flooring.plankLengthIn} htmlFor="plankLength" />
            <Input
              id="plankLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 48`}
              value={inputs.plankLength}
              onChange={handleInputChange('plankLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.flooring.plankWidthIn} htmlFor="plankWidth" />
            <Input
              id="plankWidth"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 5`}
              value={inputs.plankWidth}
              onChange={handleInputChange('plankWidth')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <Button
        fullWidth
        variant="primary"
        size="lg"
        onClick={handleCalculate}
        className="mb-6">
        {tCalc.flooring.calculateButton}
      </Button>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.results}
          </h4>
          <div className="space-y-3">
            {/* Room Area */}
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{tCalc.dimensions.roomArea}</span>{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                {results.roomArea}
              </span>{' '}
              {tCalc.dimensions.sqFt}
            </p>

            {/* Flooring Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.flooring.flooringNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.flooringNeeded}
                </span>{' '}
                {tCalc.dimensions.sqFt}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: parseFloat(results.flooringNeeded),
                      amountType: 'squareFeet',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.materials.flooring,
                      resultLabel: tCalc.flooring.flooringNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Boxes Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.flooring.boxesNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.boxesNeeded}
                </span>{' '}
                {results.boxesNeeded === 1 ? tCalc.flooring.boxesUnitOne : tCalc.flooring.boxesUnitOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.boxesNeeded,
                      amountType: 'boxes',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.flooring.flooringBoxesMaterial,
                      resultLabel: tCalc.flooring.boxesNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Planks Needed (optional) */}
            {results.planksNeeded !== null && (
              <div className="flex items-center justify-between">
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">{tCalc.flooring.planksNeeded}</span>{' '}
                  <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                    {results.planksNeeded}
                  </span>{' '}
                  {results.planksNeeded === 1 ? tCalc.flooring.planksUnitOne : tCalc.flooring.planksUnitOther}
                </p>
                {onAddToMaterial && (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() =>
                      onAddToMaterial({
                        amount: results.planksNeeded!,
                        amountType: 'units',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.flooring.flooringPlanksMaterial,
                        resultLabel: tCalc.flooring.planksNeeded,
                      })
                    }>
                    {tCalc.actions.addToMaterial}
                  </Button>
                )}
              </div>
            )}

            {/* Add All Materials Button */}
            {onAddAllMaterials && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    const allMaterials: MaterialFromCalculator[] = [
                      {
                        amount: parseFloat(results.flooringNeeded),
                        amountType: 'squareFeet',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.materials.flooring,
                        resultLabel: tCalc.flooring.flooringNeeded,
                      },
                      {
                        amount: results.boxesNeeded,
                        amountType: 'boxes',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.flooring.flooringBoxesMaterial,
                        resultLabel: tCalc.flooring.boxesNeeded,
                      },
                    ];
                    if (results.planksNeeded !== null) {
                      allMaterials.push({
                        amount: results.planksNeeded,
                        amountType: 'units',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.flooring.flooringPlanksMaterial,
                        resultLabel: tCalc.flooring.planksNeeded,
                      });
                    }
                    onAddAllMaterials(allMaterials);
                  }}>
                  {tCalc.actions.addAllMaterials} ({results.planksNeeded !== null ? 3 : 2})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlooringCalculator;
